import { useEffect, useState } from "react";
import { api, errMsg, money, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, PackageCheck, X } from "lucide-react";
import { toast } from "sonner";

const STATUS_VARIANT = {
  placed: "secondary", partial: "outline", received: "default", cancelled: "destructive",
};

export default function Purchases() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ supplier_id: "", warehouse_id: "", shipping_cost: 0, notes: "", items: [] });
  const [detail, setDetail] = useState(null);

  const fetchRows = async (pg = page) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/purchases?page=${pg}&limit=${limit}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchRows(page); /* eslint-disable-next-line */ }, [page]);
  useEffect(() => {
    api.get("/suppliers?limit=100").then((r) => setSuppliers(r.data.items || [])).catch(() => {});
    api.get("/products?limit=100").then((r) => setProducts(r.data.items || [])).catch(() => {});
    api.get("/warehouses?limit=100").then((r) => setWarehouses(r.data.items || [])).catch(() => {});
  }, []);

  const supplierName = (id) => suppliers.find((s) => s.id === id)?.name || id;
  const productName = (id) => products.find((p) => p.id === id)?.name || id;

  const openCreate = () => {
    setForm({ supplier_id: "", warehouse_id: "", shipping_cost: 0, notes: "", items: [{ product_id: "", qty_ordered: 1, unit_cost: 0, gst_rate: 0 }] });
    setOpen(true);
  };
  const addLine = () => setForm((f) => ({ ...f, items: [...f.items, { product_id: "", qty_ordered: 1, unit_cost: 0, gst_rate: 0 }] }));
  const setLine = (i, patch) => setForm((f) => ({ ...f, items: f.items.map((it, idx) => idx === i ? { ...it, ...patch } : it) }));
  const rmLine = (i) => setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        supplier_id: form.supplier_id,
        warehouse_id: form.warehouse_id || null,
        shipping_cost: Number(form.shipping_cost || 0),
        notes: form.notes || null,
        items: form.items.map((it) => ({
          product_id: it.product_id,
          qty_ordered: Number(it.qty_ordered),
          unit_cost: Number(it.unit_cost),
          gst_rate: Number(it.gst_rate || 0),
        })),
      };
      await api.post("/purchases", payload);
      toast.success("Purchase order created");
      setOpen(false); fetchRows(1); setPage(1);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const receiveAll = async (po) => {
    if (!window.confirm(`Receive all items for ${po.po_number}?`)) return;
    try {
      await api.post(`/purchases/${po.id}/receive`, {
        received_items: (po.items || []).map((it) => ({
          product_id: it.product_id,
          variant_id: it.variant_id || null,
          qty_received: Number(it.qty_ordered) - Number(it.qty_received || 0),
        })).filter((it) => it.qty_received > 0),
      });
      toast.success("Stock received");
      fetchRows(page);
      setDetail(null);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const cancel = async (po) => {
    if (!window.confirm(`Cancel ${po.po_number}?`)) return;
    try { await api.post(`/purchases/${po.id}/cancel`); toast.success("PO cancelled"); fetchRows(page); setDetail(null); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const columns = [
    { key: "po_number", header: "PO #", render: (r) => <span className="font-mono font-semibold">{r.po_number}</span> },
    { key: "supplier_id", header: "Supplier", render: (r) => supplierName(r.supplier_id) },
    { key: "items", header: "Lines", className: "w-16 text-right", render: (r) => r.items?.length ?? 0 },
    { key: "total_amount", header: "Total", className: "w-28 text-right", render: (r) => <span className="font-semibold">{money(r.total_amount)}</span> },
    { key: "status", header: "Status", className: "w-28",
      render: (r) => <Badge variant={STATUS_VARIANT[r.status] || "secondary"} className="capitalize">{r.status}</Badge> },
    { key: "created_at", header: "Created", render: (r) => <span className="text-xs text-muted-foreground">{fmtDate(r.created_at)}</span> },
  ];

  return (
    <div className="p-8" data-testid="purchases-page">
      <PageHeader title="Purchase Orders" subtitle="Stock arriving from suppliers"
        actions={<Button onClick={openCreate} className="btn-primary-glow" data-testid="add-po-btn"><Plus className="w-4 h-4 mr-2" />New PO</Button>} />

      <DataTable columns={columns} rows={rows} loading={loading} onRowClick={setDetail}
        page={page} limit={limit} total={total} onPage={setPage} testId="purchases-table" />

      {/* Create PO */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="po-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">New Purchase Order</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Supplier *</Label>
              <Select value={form.supplier_id} onValueChange={(v) => setForm({ ...form, supplier_id: v })}>
                <SelectTrigger data-testid="po-supplier"><SelectValue placeholder="Choose supplier…" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Warehouse</Label>
              <Select value={form.warehouse_id || "__none"} onValueChange={(v) => setForm({ ...form, warehouse_id: v === "__none" ? "" : v })}>
                <SelectTrigger><SelectValue placeholder="Default" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">— default —</SelectItem>
                  {warehouses.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <Label>Line items</Label>
              <Button variant="outline" size="sm" onClick={addLine} data-testid="po-add-line"><Plus className="w-3 h-3 mr-1" /> Add line</Button>
            </div>
            <div className="space-y-2">
              {form.items.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end">
                  <div className="col-span-5">
                    {i === 0 && <Label className="text-xs">Product</Label>}
                    <Select value={it.product_id} onValueChange={(v) => setLine(i, { product_id: v, unit_cost: products.find((p) => p.id === v)?.cost_price || products.find((p) => p.id === v)?.purchase_price || 0, gst_rate: products.find((p) => p.id === v)?.gst_rate || 0 })}>
                      <SelectTrigger data-testid={`po-line-${i}-product`}><SelectValue placeholder="Select product" /></SelectTrigger>
                      <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">Qty</Label>}
                    <Input type="number" min="1" value={it.qty_ordered} onChange={(e) => setLine(i, { qty_ordered: e.target.value })} data-testid={`po-line-${i}-qty`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">Unit cost</Label>}
                    <Input type="number" step="0.01" value={it.unit_cost} onChange={(e) => setLine(i, { unit_cost: e.target.value })} data-testid={`po-line-${i}-cost`} />
                  </div>
                  <div className="col-span-2">
                    {i === 0 && <Label className="text-xs">GST %</Label>}
                    <Input type="number" value={it.gst_rate} onChange={(e) => setLine(i, { gst_rate: e.target.value })} />
                  </div>
                  <div className="col-span-1">
                    <Button variant="ghost" size="icon" onClick={() => rmLine(i)} disabled={form.items.length === 1}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-4">
            <div><Label>Shipping cost</Label><Input type="number" step="0.01" value={form.shipping_cost} onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.supplier_id || form.items.some((it) => !it.product_id || !it.qty_ordered)} className="btn-primary-glow" data-testid="save-po-btn">
              {saving ? "Creating…" : "Create PO"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="po-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-3">
                  {detail.po_number}
                  <Badge variant={STATUS_VARIANT[detail.status] || "secondary"} className="capitalize">{detail.status}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="text-sm space-y-1">
                <div><span className="text-muted-foreground">Supplier:</span> {supplierName(detail.supplier_id)}</div>
                <div><span className="text-muted-foreground">Created:</span> {fmtDate(detail.created_at)}</div>
                <div><span className="text-muted-foreground">Total:</span> <span className="font-semibold">{money(detail.total_amount)}</span></div>
              </div>
              <div className="border rounded-lg divide-y mt-3">
                {(detail.items || []).map((it, i) => (
                  <div key={i} className="p-3 flex items-center gap-3">
                    <div className="flex-1 min-w-0"><div className="font-medium truncate">{productName(it.product_id)}</div></div>
                    <div className="text-xs text-muted-foreground">Qty {it.qty_received || 0}/{it.qty_ordered}</div>
                    <div className="w-24 text-right font-semibold">{money(it.line_total)}</div>
                  </div>
                ))}
              </div>
              <DialogFooter>
                {detail.status !== "cancelled" && detail.status !== "received" && (
                  <>
                    <Button variant="outline" onClick={() => cancel(detail)} data-testid="po-cancel-btn"><X className="w-4 h-4 mr-1" />Cancel</Button>
                    <Button onClick={() => receiveAll(detail)} className="btn-primary-glow" data-testid="po-receive-btn"><PackageCheck className="w-4 h-4 mr-1" />Receive all</Button>
                  </>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
